import React from "react";
import { Loader2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface DeleteTemplateButtonProps {
  templateName: string;
  /** Why the template cannot be deleted right now; null when it can. */
  unavailableReason: string | null;
  busy: boolean;
  onConfirm: () => void;
}

/**
 * Delete icon shared by the list and the editor. When deletion is unavailable it
 * stays visible and does nothing, with the reason in a tooltip. It is aria-disabled
 * rather than disabled so hover and focus still reach the tooltip.
 */
const DeleteTemplateButton: React.FC<DeleteTemplateButtonProps> = ({
  templateName,
  unavailableReason,
  busy,
  onConfirm,
}) => {
  const label = unavailableReason
    ? `Delete ${templateName} (unavailable)`
    : `Delete ${templateName}`;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={label}
            aria-disabled={Boolean(unavailableReason) || busy}
            aria-busy={busy}
            className={cn(
              "text-muted-foreground",
              unavailableReason
                ? "cursor-not-allowed opacity-60 hover:bg-transparent hover:text-muted-foreground"
                : "cursor-pointer hover:bg-destructive/10 hover:text-destructive focus-visible:ring-destructive/40"
            )}
            onClick={() => {
              if (unavailableReason || busy) return;
              if (!window.confirm(`Delete "${templateName}"? This removes the template.`)) return;
              onConfirm();
            }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">
          {unavailableReason ?? "Delete template"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default DeleteTemplateButton;
